import type { ReactNode } from "react";
import { ONBOARDING_STEPS, stepIndex, type OnboardingStep } from "../../lib/onboarding";

interface Props {
  children: ReactNode;
  currentStep: OnboardingStep;
  deviceName?: string;
}

// Full-page onboarding shell. Real UB logo mark upper-left, wordmark right of it.
// Custom step counter "01 / 06" upper-right instead of bars for a more editorial feel.
// Footer keeps a subtle device name and keyboard hint.
export function OnboardingLayout({ children, currentStep, deviceName }: Props) {
  const idx = stepIndex(currentStep);
  const total = ONBOARDING_STEPS.length;
  const currentNum = String(idx + 1).padStart(2, "0");
  const totalNum = String(total).padStart(2, "0");

  return (
    <div className="min-h-screen flex flex-col">
      <header className="pt-8 pb-4">
        <div className="container flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5 group" aria-label="UB Remote home">
            <img src="/logo.svg" alt="" width="28" height="28" className="w-7 h-7" />
            <span className="font-display text-[17px] font-semibold tracking-tight text-foreground">
              UB <span className="text-primary">Remote</span>
            </span>
          </a>
          <div className="flex items-center gap-3 text-[11px] font-mono text-muted-foreground tabular-nums">
            <span className="text-foreground font-medium">{currentNum}</span>
            <span className="text-ink3">/</span>
            <span>{totalNum}</span>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center">
        <div className="container">
          <div className="max-w-[600px] mx-auto">
            {children}
          </div>
        </div>
      </main>

      <footer className="pb-8">
        <div className="container flex items-center justify-between text-[11px] text-muted-foreground">
          {/* TODO: sourced from Device step (PR #4). Placeholder until then. */}
          <span className="font-mono tabular-nums">{deviceName ?? "This device"}</span>
          <span className="hidden sm:inline">Design prototype - sample data only</span>
        </div>
      </footer>
    </div>
  );
}
